"use client";
import checkAddToCart, { AddToCartStatus } from "@/app/ec/product/[basePath]/checkAddToCart";
import PriceAndShipping from "@/app/ec/product/[basePath]/priceAndShipping";
import StockAndShippingMessage from "@/app/ec/product/[basePath]/stockAndShippingMessage";
import React, { FunctionComponent, useEffect, useState } from "react";
import { Overlay, Popover } from "react-bootstrap";
import styles from "./vendorPicker.module.scss";

const VendorPicker: FunctionComponent<VendorPickerProps> = ({
  vendorOptions,
  quantity,
  chosenVendorKey,
  setChosenVendorKey,
  targetRef,
  visible,
  hideCallback,
  selectShippingClassName,
  selectPriceClassName,
  vendorOptionsAreNotFetched,
}) => {
  const [isVisible, setIsVisible] = useState(visible);

  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);

  return (
    <Overlay
      rootClose
      flip
      popperConfig={{ modifiers: [{ name: "preventOverflow", options: { altAxis: true } }] }}
      onHide={hideCallback}
      show={isVisible && !vendorOptionsAreNotFetched}
      placement="bottom-start"
      target={targetRef.current}
    >
      <Popover className={styles.VendorPickerPopover}>
        <Popover.Body>
          <div className="d-table w-100">
            {vendorOptions.map((vendorOption, index) => {
              const isAvailable =
                checkAddToCart(quantity, vendorOption.minimumQuantityPurchasable, vendorOption.inventory) ===
                AddToCartStatus.OK;
              const rowClass = [
                styles.pickerOption,
                "d-table-row",
                chosenVendorKey === index ? styles.chosenVendor : "",
                isAvailable ? "" : styles.disabled,
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <div
                  key={vendorOption.vendorProductId}
                  className={rowClass}
                  onClick={() => {
                    if (isAvailable) {
                      setChosenVendorKey(index);
                      hideCallback();
                    }
                  }}
                >
                  <div className="d-table-cell p-2 pe-0">
                    <div className={styles.vendorInfo}>
                      <strong className={styles.vendorName}>{vendorOption.vendorName}</strong>
                      <StockAndShippingMessage
                        vendorOption={vendorOption}
                        stockMessageClassName={styles.stockMessage}
                        longHandlingTimeClassName="d-block"
                        businessDaysClassName="fw-bold"
                      />
                    </div>
                  </div>
                  <div className={`${styles.priceAndShipping} d-table-cell p-2 ps-0`}>
                    <div className={styles.vendorInfo}>
                      <div>
                        <PriceAndShipping
                          priceSize="md"
                          showShipping
                          shippingClassName={`${selectShippingClassName ?? ""} d-block ${styles.shippingText}`}
                          priceClassName={`${selectPriceClassName ?? ""} ${styles.price}`}
                          eachClassName="medium md"
                          vendorOption={vendorOption}
                          shortenShippingMessage
                          isVendorPicker
                        />
                        {vendorOption.chosenPriceBreakMinQty > 1 && (
                          <div className={styles.qtyMessage}>min qty {vendorOption.chosenPriceBreakMinQty}</div>
                        )}

                        {vendorOption.inventory < quantity && (
                          <div className={styles.qtyMessage}>max qty {vendorOption.inventory}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Popover.Body>
      </Popover>
    </Overlay>
  );
};
export default VendorPicker;

type VendorPickerProps = {
  vendorOptions: VendorOptionState[];
  quantity: number;
  chosenVendorKey: number;
  setChosenVendorKey: React.Dispatch<React.SetStateAction<number>> | ((index: number) => void);
  targetRef: React.RefObject<HTMLElement | null>;
  visible: boolean;
  hideCallback: () => void;
  selectShippingClassName?: string;
  selectPriceClassName?: string;
  vendorOptionsAreNotFetched?: boolean;
};
